import type { Client, FormattedExecutionResult, Sink, SubscribePayload } from 'graphql-ws'
import { z } from 'zod'

import { retryDelay, scheduleRetry, wasStable } from './subscriptionRetry'

const subscriptionErrorsSchema = z
  .array(
    z.object({
      message: z.string(),
      extensions: z.object({ code: z.string().optional() }).optional(),
    }),
  )
  .nonempty()

function isRecoverableSubscriptionError(error: unknown): boolean {
  const parsed = subscriptionErrorsSchema.safeParse(error)
  return (
    parsed.success &&
    parsed.data.every(({ message, extensions }) => {
      if (extensions?.code !== undefined) {
        return [
          'INTERNAL_SERVER_ERROR',
          'SERVICE_UNAVAILABLE',
          'GATEWAY_TIMEOUT',
        ].includes(extensions.code)
      }
      return message === 'failed to prepare subscription trigger'
    })
  )
}

function isUnauthorizedSubscriptionError(error: unknown): boolean {
  const parsed = subscriptionErrorsSchema.safeParse(error)
  return (
    parsed.success &&
    parsed.data.every(({ message, extensions }) =>
      extensions?.code !== undefined
        ? extensions.code === 'UNAUTHENTICATED'
        : message === 'Unauthorized',
    )
  )
}

export function subscribeWithRecovery<Data, Extensions>(
  client: Client,
  payload: SubscribePayload,
  sink: Sink<FormattedExecutionResult<Data, Extensions>>,
  {
    onRestart,
    onRelease,
    onUnauthorized,
  }: {
    onRestart: () => void
    onRelease: () => void
    onUnauthorized: () => Promise<boolean>
  },
): () => void {
  let released = false
  let restartAttempts = 0
  let firstDataAt: number | undefined
  let cancelAttempt: (() => void) | undefined
  const release = () => {
    if (released) {
      return
    }
    released = true
    cancelAttempt?.()
    onRelease()
  }
  const restart = () => {
    if (released) {
      return
    }
    if (wasStable(firstDataAt)) {
      restartAttempts = 0
    }
    cancelAttempt = scheduleRetry(() => {
      start()
      if (restartAttempts === 1) {
        onRestart()
      }
    }, retryDelay(restartAttempts++))
  }
  const handleUnauthorized = async (onFailure: () => void) => {
    const recovered = await onUnauthorized()
    if (released) {
      return
    }
    if (recovered) {
      restart()
    } else {
      release()
      onFailure()
    }
  }
  const start = () => {
    firstDataAt = undefined
    let ended = false
    let lastResultFailed = false
    cancelAttempt = client.subscribe<Data, Extensions>(payload, {
      next: (result) => {
        if (ended || released) {
          return
        }
        if (result.data == null && isUnauthorizedSubscriptionError(result.errors)) {
          ended = true
          cancelAttempt?.()
          void handleUnauthorized(() => {
            sink.next(result)
            sink.complete()
          })
          return
        }
        if (result.data == null && isRecoverableSubscriptionError(result.errors)) {
          ended = true
          cancelAttempt?.()
          restart()
          return
        }
        if (result.data != null) {
          firstDataAt ??= performance.now()
        }
        lastResultFailed = result.data == null && Boolean(result.errors?.length)
        sink.next(result)
      },
      error: (error) => {
        if (ended || released) {
          return
        }
        ended = true
        if (isUnauthorizedSubscriptionError(error)) {
          void handleUnauthorized(() => sink.error(error))
        } else if (isRecoverableSubscriptionError(error)) {
          restart()
        } else {
          release()
          sink.error(error)
        }
      },
      complete: () => {
        if (!ended) {
          ended = true
          if (lastResultFailed) {
            release()
            sink.complete()
          } else {
            restart()
          }
        }
      },
    })
  }
  start()
  return release
}
